import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, TextField, MenuItem, Tooltip, IconButton, Tab, Tabs,
  LinearProgress, Divider, List, ListItem, ListItemText,
} from '@mui/material';
import { Favorite, Refresh, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const YEAR = new Date().getFullYear();

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const BAND_CFG: Record<string, { label: string; color: string; bg: string; emoji: string }> = {
  highly_engaged: { label: 'Highly Engaged', color: '#1b5e20', bg: '#e8f5e9', emoji: '🌟' },
  engaged:        { label: 'Engaged',         color: '#2e7d32', bg: '#f1f8e9', emoji: '😊' },
  neutral:        { label: 'Neutral',         color: '#f57f17', bg: '#fffde7', emoji: '😐' },
  disengaged:     { label: 'Disengaged',      color: '#e65100', bg: '#fff3e0', emoji: '😟' },
  at_risk:        { label: 'At Risk',         color: '#b71c1c', bg: '#ffebee', emoji: '⚠️' },
};

const SIGNAL_COLORS: Record<string, string> = {
  wellness:    '#00838f',
  survey_nps:  '#6a1b9a',
  kudos:       '#C08A2E',
  leave_util:  '#1565c0',
  training:    '#2e7d32',
  absenteeism: '#e65100',
  retention:   '#37474f',
};

function ScoreGauge({ score, size = 80 }: { score: number; size?: number }) {
  const color = score >= 80 ? '#1b5e20' : score >= 65 ? '#2e7d32' : score >= 50 ? '#f57f17' : score >= 35 ? '#e65100' : '#b71c1c';
  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const arc  = circ * 0.75;
  const offset = arc - (arc * score) / 100;
  return (
    <Box sx={{ position: 'relative', width: size, height: size * 0.75, overflow: 'visible' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-225deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e0e0e0" strokeWidth={6}
          strokeDasharray={`${arc} ${circ - arc}`} strokeLinecap="round" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={7}
          strokeDasharray={`${arc - offset} ${circ - (arc - offset)}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.8s ease' }} />
      </svg>
      <Box sx={{ position: 'absolute', top: '40%', left: 0, width: size, textAlign: 'center', transform: 'translateY(-50%)' }}>
        <Typography variant="h6" fontWeight={800} color={color} sx={{ fontSize: size * 0.22 }}>{score}</Typography>
      </Box>
    </Box>
  );
}

function SignalBar({ signal, score, weight, label, interpretation, unit, value, direction }: any) {
  const color = SIGNAL_COLORS[signal] ?? '#546e7a';
  const isGood = direction === 'higher_better' ? score >= 65 : score >= 65;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
          <Chip size="small" label={`${weight}%`}
            sx={{ bgcolor: color + '15', color, fontSize: '0.55rem', height: 16 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {value !== null && <Typography variant="caption" color="text.secondary">{parseFloat(value).toFixed(1)}{unit}</Typography>}
          <Chip size="small" label={`${score}/100`}
            sx={{ bgcolor: score >= 65 ? '#e8f5e9' : score >= 40 ? '#fff3e0' : '#ffebee',
              color: score >= 65 ? '#2e7d32' : score >= 40 ? '#e65100' : '#b71c1c',
              fontWeight: 700, fontSize: '0.65rem' }} />
        </Box>
      </Box>
      <LinearProgress variant="determinate" value={score}
        sx={{ height: 6, borderRadius: 3, bgcolor: '#f5f5f5',
          '& .MuiLinearProgress-bar': { bgcolor: score >= 65 ? color : score >= 40 ? '#f57f17' : '#ef5350' } }} />
      <Typography variant="caption" color="text.secondary">{interpretation}</Typography>
    </Box>
  );
}

export default function EngagementScore() {
  const [year, setYear]       = useState(YEAR);
  const [tab, setTab]         = useState(0);
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch(`/api/engagement-score?year=${year}`)); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const overall = data?.orgOverall;
  const depts   = data?.deptScores ?? [];
  const signals = data?.signalBreakdown ?? [];
  const trend   = data?.monthlyTrend ?? [];
  const maxWellness = Math.max(...trend.map((t: any) => parseFloat(t.wellnessScore || 0)), 1);

  const atRisk     = depts.filter((d: any) => d.band === 'at_risk').length;
  const disengaged = depts.filter((d: any) => d.band === 'disengaged').length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#c62828', width: 48, height: 48 }}><Favorite /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Employee Engagement Score</Typography>
            <Typography variant="body2" color="text.secondary">
              Composite index from wellness, NPS, recognition, leave, training, absenteeism & retention
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField select size="small" label="Year" value={year}
            onChange={e => setYear(parseInt(e.target.value))} sx={{ width: 90 }}>
            {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {(atRisk > 0 || disengaged > 0) && (
        <Alert severity={atRisk > 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {atRisk > 0 && <><strong>{atRisk} department{atRisk > 1 ? 's are' : ' is'} at risk</strong> — urgent engagement intervention needed. </>}
          {disengaged > 0 && <><strong>{disengaged} department{disengaged > 1 ? 's' : ''} disengaged</strong> — action recommended.</>}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Overall org score */}
          {overall && (
            <Paper sx={{ p: 2, mb: 3, border: `2px solid ${BAND_CFG[overall.band]?.color ?? '#e0e0e0'}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2 }}>
                    <ScoreGauge score={overall.overall} size={100} />
                    <Chip label={`${BAND_CFG[overall.band]?.emoji} ${BAND_CFG[overall.band]?.label}`}
                      sx={{ mt: 0.5, bgcolor: BAND_CFG[overall.band]?.bg, color: BAND_CFG[overall.band]?.color, fontWeight: 700 }} />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 9 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Organisation-wide Engagement</Typography>
                  <Grid container spacing={1}>
                    {Object.entries(overall.components).map(([k, v]: [string, any]) => {
                      const color = SIGNAL_COLORS[k] ?? '#546e7a';
                      const label = signals.find((s: any) => s.signal === k)?.label ?? k;
                      return (
                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={k}>
                          <Box sx={{ p: 1, borderRadius: 1, bgcolor: color + '12', borderLeft: `3px solid ${color}` }}>
                            <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
                            <Typography variant="body2" fontWeight={700} color={color}>{Math.round(v)}/100</Typography>
                            <LinearProgress variant="determinate" value={v}
                              sx={{ height: 3, mt: 0.25, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab label="By Department" />
            <Tab label="Signal Weights" />
            <Tab label="Wellness Trend" />
          </Tabs>

          {tab === 0 && (
            <Grid container spacing={2}>
              {depts.map((d: any) => {
                const bc = BAND_CFG[d.band] ?? BAND_CFG.neutral;
                return (
                  <Grid size={{ xs: 12, md: 6 }} key={d.department}>
                    <Paper sx={{ p: 2, borderLeft: `4px solid ${bc.color}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                        <ScoreGauge score={d.overallScore} size={72} />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography variant="subtitle2" fontWeight={700}>{d.department}</Typography>
                            <Chip size="small" label={`${d.headcount} staff`} sx={{ fontSize: '0.6rem' }} />
                          </Box>
                          <Chip size="small" label={`${bc.emoji} ${bc.label}`}
                            sx={{ bgcolor: bc.bg, color: bc.color, fontWeight: 700, fontSize: '0.65rem', mb: 0.5 }} />
                          {/* Top weak signals */}
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(d.signals ?? []).filter((s: any) => s.score < 50).slice(0, 2).map((s: any) => (
                              <Chip key={s.signal} size="small" label={`⚠ ${s.label}`}
                                sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontSize: '0.55rem', height: 18 }} />
                            ))}
                            {(d.signals ?? []).filter((s: any) => s.score >= 80).slice(0, 1).map((s: any) => (
                              <Chip key={s.signal} size="small" label={`✓ ${s.label}`}
                                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.55rem', height: 18 }} />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      <Divider sx={{ mb: 1 }} />
                      {(d.signals ?? []).map((s: any) => (
                        <SignalBar key={s.signal} {...s} />
                      ))}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {tab === 1 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>How the Score is Calculated</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Seven signals are weighted and combined into a 0–100 engagement score. Higher is better (except absenteeism where lower attendance absence = higher score).
                  </Typography>
                  {signals.map((s: any) => {
                    const color = SIGNAL_COLORS[s.signal] ?? '#546e7a';
                    return (
                      <Box key={s.signal} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
                            <Typography variant="body2" fontWeight={600}>{s.label}</Typography>
                          </Box>
                          <Chip size="small" label={`${s.weight}% weight`}
                            sx={{ bgcolor: color + '18', color, fontWeight: 700, fontSize: '0.65rem' }} />
                        </Box>
                        <LinearProgress variant="determinate" value={s.weight}
                          sx={{ height: 5, mt: 0.5, borderRadius: 3, bgcolor: '#f5f5f5',
                            '& .MuiLinearProgress-bar': { bgcolor: color } }} />
                        <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                      </Box>
                    );
                  })}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      🟢 Highly Engaged ≥80 &nbsp;·&nbsp; 😊 Engaged 65–79 &nbsp;·&nbsp; 😐 Neutral 50–64 &nbsp;·&nbsp; 😟 Disengaged 35–49 &nbsp;·&nbsp; ⚠️ At Risk &lt;35
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Department Score Ranking</Typography>
                  {depts.map((d: any, i: number) => {
                    const bc = BAND_CFG[d.band] ?? BAND_CFG.neutral;
                    return (
                      <Box key={d.department} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem',
                          bgcolor: i < 3 ? bc.color : '#e0e0e0', color: i < 3 ? '#fff' : '#546e7a' }}>
                          {i + 1}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{d.department}</Typography>
                          <LinearProgress variant="determinate" value={d.overallScore}
                            sx={{ height: 6, borderRadius: 3,
                              '& .MuiLinearProgress-bar': { bgcolor: bc.color } }} />
                        </Box>
                        <Chip size="small" label={`${d.overallScore}`}
                          sx={{ bgcolor: bc.bg, color: bc.color, fontWeight: 700, minWidth: 36 }} />
                      </Box>
                    );
                  })}
                </Paper>
              </Grid>
            </Grid>
          )}

          {tab === 2 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Monthly Wellness Score Trend ({year})</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Based on weekly wellness check-in mood scores (1–5 scale → 0–100)
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 140, mb: 1 }}>
                {trend.map((t: any) => {
                  const h = maxWellness > 0 ? Math.max(4, (parseFloat(t.wellnessScore) / 100) * 130) : 4;
                  const color = parseFloat(t.wellnessScore) >= 70 ? '#2e7d32' : parseFloat(t.wellnessScore) >= 50 ? '#f57f17' : '#b71c1c';
                  return (
                    <Tooltip key={t.month} title={`${t.monthLabel}: ${t.wellnessScore}/100 (${t.checkinCount} check-ins)`}>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Box sx={{ width: '70%', height: h, bgcolor: color, borderRadius: '3px 3px 0 0', opacity: 0.85 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>{t.monthLabel}</Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              {/* Target line annotation */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Chip size="small" label="🟢 Healthy ≥70" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }} />
                <Chip size="small" label="🟡 Moderate 50–69" sx={{ bgcolor: '#fffde7', color: '#f57f17', fontSize: '0.65rem' }} />
                <Chip size="small" label="🔴 Concern <50" sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontSize: '0.65rem' }} />
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
